import { screenAxes } from "./cameraProject";
import type { IsoCamera } from "./isoCamera";

/**
 * Pinch-to-zoom on the hub (owner 2026-10-24: "we should be able to pinch to zoom on the Home Screen"). The hub's still
 * shot (HubLayout.frame) is the default; a pinch eases in about the fingers to a closer look at the fortress and the
 * camp, or out a little past the default, through the level camera's own eased pinch (cameraMotion.ts CameraTouch).
 *
 * The rules (pure, test/hubZoom.test.ts):
 * - the zoom stays within `InShare`..`OutShare` of the default shot's zoom;
 * - the view never leaves what the default shot shows: zoomed in by a factor k of the default, the look point may move
 *   (pinching off-centre, or dragging) at most (1 − k) of the default view's half extent along each screen axis, so the
 *   edges of the view stay inside the default view's; at or past the default zoom it is held on the default look point.
 *   The default shot is framed inside the UI's band and over the island, so neither the void past the haze nor the
 *   band under the top bar and the battle card can open up.
 *
 * The view the player left (zoom and offset from the default shot) is kept for the session, so a battle and back, or a
 * re-frame when the battle card changes height, lands where they were.
 */
export const HUB_ZOOM = {
  /** Closest zoom, as a share of the default shot's (camera distance): 0.45 is ~2.2× closer. */
  InShare: 0.45,
  /** Farthest, as a share of the default: a little more of the camp than the default shot. */
  OutShare: 1.12,
} as const;

/** The default shot: the look point and the zoom HubLayout.frame chose, and the view's half extents there (cells). */
export interface HubShot {
  readonly x: number;
  readonly z: number;
  readonly zoom: number;
  readonly half: { readonly right: number; readonly forward: number };
  readonly yaw: number;
}

/** The hub's zoom range for a default shot at `zoom`. */
export function hubZoomRange(zoom: number): { min: number; max: number } {
  return { min: zoom * HUB_ZOOM.InShare, max: zoom * HUB_ZOOM.OutShare };
}

/** The look point (x, z) at `zoom` held inside the default shot's view; null when it already is. */
export function clampHubLook(shot: HubShot, x: number, z: number, zoom: number): { x: number; z: number } | null {
  const { right, forward } = screenAxes(shot.yaw);
  const dx = x - shot.x, dz = z - shot.z;
  const u = dx * right.x + dz * right.z, v = dx * forward.x + dz * forward.z;
  const room = Math.max(0, 1 - zoom / shot.zoom);
  const cu = clamp(u, room * shot.half.right), cv = clamp(v, room * shot.half.forward);
  if (Math.abs(cu - u) < 1e-9 && Math.abs(cv - v) < 1e-9) return null;
  return { x: shot.x + right.x * cu + forward.x * cv, z: shot.z + right.z * cu + forward.z * cv };
}

function clamp(a: number, r: number): number { return Math.min(r, Math.max(-r, a)); }

/** The player's view relative to the default shot: zoom as a share of it, and the look point's offset along the screen axes. */
export interface HubView { readonly ratio: number; readonly u: number; readonly v: number }

/** The hub camera's zoom state across re-frames and visits (HubScene owns one per hub). */
export class HubZoom {
  /** The view the player left, for the session. */
  static session: HubView | null = null;
  private shot: HubShot | null = null;
  private last = { x: NaN, z: NaN, zoom: NaN };

  constructor(private readonly iso: IsoCamera) {}

  /** Before the shot is re-framed: the view is remembered and the hub's limits dropped, so the fit is unconstrained. */
  release(): void {
    this.remember();
    this.shot = null;
    this.iso.setZoomLimits(null);
  }

  /** After the shot is framed: it is the default; the range and bounds go on and the remembered view comes back. */
  capture(): void {
    const look = this.iso.lookPoint(), zoom = this.iso.currentZoom;
    const shot: HubShot = { x: look.x, z: look.z, zoom, half: { right: this.iso.cellsAcross() / 2, forward: this.iso.cellsDeep() / 2 }, yaw: this.iso.currentYaw };
    this.shot = shot;
    this.iso.setZoomLimits(hubZoomRange(zoom), (x, z, zm) => clampHubLook(shot, x, z, zm));
    const s = HubZoom.session;
    if (!s) return;
    const { right, forward } = screenAxes(shot.yaw);
    this.iso.restoreView({ x: shot.x + right.x * s.u + forward.x * s.v, z: shot.z + right.z * s.u + forward.z * s.v, zoom: zoom * s.ratio, yaw: shot.yaw });
  }

  /** Whether the view moved since the last call (the chips pinned to the models follow it). */
  moved(): boolean {
    const look = this.iso.lookPoint(), zoom = this.iso.currentZoom, l = this.last;
    if (look.x === l.x && look.z === l.z && zoom === l.zoom) return false;
    this.last = { x: look.x, z: look.z, zoom };
    return true;
  }

  /** The hub closes: its view is kept for the next visit and the camera gets the level's range back. */
  dispose(): void { this.release(); }

  private remember(): void {
    const shot = this.shot;
    if (!shot) return;
    const look = this.iso.lookPoint(), { right, forward } = screenAxes(shot.yaw);
    const dx = look.x - shot.x, dz = look.z - shot.z;
    HubZoom.session = { ratio: this.iso.currentZoom / shot.zoom, u: dx * right.x + dz * right.z, v: dx * forward.x + dz * forward.z };
  }
}
